
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { analytics } from '@/services/analytics/analyticsService';

interface SignOutProps {
  setIsLoading: (loading: boolean) => void;
  setUser: (user: any) => void;
  setSession: (session: any) => void;
  setIsAdmin: (isAdmin: boolean) => void;
}

export const useSignOut = ({
  setIsLoading,
  setUser,
  setSession,
  setIsAdmin
}: SignOutProps) => {
  
  const signOut = async () => {
    try {
      setIsLoading(true);
      console.log('Signing out user');
      
      // Track sign out event before clearing user
      analytics.trackAuthEvent('sign_out', 'user_initiated');
      
      // First, clear any auth in progress flags
      sessionStorage.removeItem('googleAuthInProgress');
      
      // Clear local state first for better UX
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      
      // Clear analytics user ID
      analytics.setUserId(null);
      
      // Clear any local/session storage related to authentication
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');
      
      // Clear any additional cached data
      localStorage.removeItem('adminStatus');
      sessionStorage.removeItem('adminStatus');
      
      // Clear any browser cache for auth routes
      // This helps with preventing stale data after logout
      if ('caches' in window) {
        try {
          const authCaches = await window.caches.keys();
          const authRelatedCaches = authCaches.filter(name => 
            name.includes('auth') || name.includes('user') || name.includes('admin')
          );
          
          await Promise.all(
            authRelatedCaches.map(cacheName => window.caches.delete(cacheName))
          );
          console.log('Auth-related caches cleared');
        } catch (cacheError) {
          console.warn('Error clearing browser caches:', cacheError);
        }
      }
      
      // Now perform the actual sign out operation
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase sign out error:', error);
        throw error;
      }
      
      console.log('Sign out successful');
      
      toast.success("Signed out", {
        description: "You have been successfully signed out.",
      });
      
      // Use an extra timeout to ensure all state is cleared before navigating
      setTimeout(() => {
        // Use window.location for a full page reload to clear any cached auth state
        window.location.href = '/';
      }, 100);
    } catch (error: any) {
      console.error('Sign out error details:', error);
      toast.error("Error signing out", {
        description: error.message || "An unexpected error occurred.",
      });
      
      // Force reload the page anyway if there was an error
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } finally {
      setIsLoading(false);
    }
  };

  return { signOut };
};
